import { pushAction, inProgress, type HistoryAction } from '../../features/history/undoHistory.ts';
import { internalData } from '../../store/store.ts';

type Constructor<T = HTMLElement> = new (...args: any[]) => T;
type AbstractConstructor<T = HTMLElement> = abstract new (...args: any[]) => T;

export function HistoryTrackingMixin<TBase extends AbstractConstructor>(Base: TBase) {
  abstract class WithHistoryTracking extends Base {
    /** Signals to as-configurator that this element self-manages history. */
    readonly _historyManaged = true;

    abstract _getHistoryValue(): any;

    /** Override to return true when pushing history should also clear the redo tail. */
    _historyEntryClearsRedo(): boolean { return false; }

    /** Override to return true while another record covers this input's edits,
     *  an appData binding for instance. The input then pushes no entry. */
    _historyOwnedElsewhere(): boolean { return false; }

    /** Set true before calling setValue/internal-set to skip the next history push. Always cleared after. */
    _skipNextHistoryPush = false;

    readonly _handleOwnInputChange = (_e: Event): void => {
      const skip = this._skipNextHistoryPush;
      this._skipNextHistoryPush = false;
      if (skip) return;
      // Only a real edit belongs on the undo stack. Everything else that moves
      // a value — a project load, an undo, a param sync, a reset — reports on
      // the sync channel, but an explicit setValue() still lands here, so check
      // the reason rather than trusting the channel alone.
      const reason = (_e as CustomEvent)?.detail?.reason;
      if (reason && reason !== 'user' && reason !== 'api') return;
      if (inProgress()) return;
      if (this.hasAttribute('ignore-history') || this.hasAttribute('ignore-reset')) return;
      if (this.hasAttribute('trcad-param') && this.hasAttribute('configure-on-change')) return;
      if (this._historyOwnedElsewhere()) return;
      if (!this.id) return;

      const valueBefore = internalData.uiInputs[this.id] ?? null;
      const valueAfter = this._getHistoryValue();
      if (valueAfter === valueBefore) return;

      const action: HistoryAction = {
        type: 'input',
        inputId: this.id,
        valueBefore,
        valueAfter,
        ...(this._historyEntryClearsRedo() ? { clearsRedoOnUndo: true } : {}),
      };
      // The entry lands once every listener of this change event has run. A
      // handler that opens a batch and awaits its configure then holds the
      // entry too, and undo puts the input and the params back in one step.
      // The values are read now, so a handler that clears the input afterwards
      // does not change what is recorded.
      queueMicrotask(() => pushAction(action));
    };

    _registerHistoryTracking(): void {
      this.addEventListener('as-input-change', this._handleOwnInputChange);
    }

    _unregisterHistoryTracking(): void {
      this.removeEventListener('as-input-change', this._handleOwnInputChange);
    }
  }
  return WithHistoryTracking;
}
